import { useId, type ReactNode, type Ref } from 'react';
import { ArrowRight, MessageCircleMore, PenLine } from 'lucide-react';
import { RECOMMENDED_GUIDED_INTERVIEW_VERSION } from '@volley/crucible/intake/interview-version.js';
import type { GuidedInterviewVersion } from '@volley/crucible-ipc-types';
import { Select } from './ui/select';
/**
 * The create flow's full kickoff-mode value space: the two live interview
 * depths plus the renderer-only `'just-build'` mode (#1773 — mapped to
 * `interviewDepth: 'quick'` + `interviewBestEffort: true` on the
 * `game:create` payload; never written to the state file as a depth).
 *
 * Home moved here from the deleted legacy `CreateGameInterviewDepthToggle`
 * component (crucible#3313) — this selector is the one live kickoff picker.
 * Deliberately NOT derived from the persisted `KnownInterviewDepth`: that
 * taxonomy keeps the legacy `'full'` literal for READING old
 * `build-game-interview.json` state files, but the kickoff space (and the
 * `game:create` wire) retired `'full'` — see crucible#2637 / crucible#3313.
 */
export type CreateGameKickoffMode = 'quick' | 'advanced' | 'just-build';

export type VisibleCreateKickoffMode = Extract<CreateGameKickoffMode, 'quick' | 'advanced'>;

const MODE_COPY: Record<
  VisibleCreateKickoffMode,
  { label: string; description: string; action: string }
> = {
  quick: {
    label: 'Guided interview',
    description: 'Answer a few focused questions, then review the plan before you start building.',
    action: 'Start interview',
  },
  advanced: {
    label: 'Plan in Design workspace',
    description: 'Shape the game plan and visual direction before building.',
    action: 'Open Design workspace',
  },
};

interface GuidedInterviewExperienceSelectorProps {
  readonly compact: boolean;
  readonly value: GuidedInterviewVersion;
  readonly onChange: (version: GuidedInterviewVersion) => void;
}

const GUIDED_INTERVIEW_OPTIONS: ReadonlyArray<{
  readonly value: GuidedInterviewVersion;
  readonly label: string;
  readonly description: string;
}> = [
  {
    value: 'v3',
    label: 'Version 3 — Focused',
    description: 'Concise questions and choices with less repetition',
  },
  {
    value: 'v2',
    label: 'Version 2 — Adaptive',
    description: 'Direct questions that build on your answers',
  },
  {
    value: 'v1',
    label: 'Version 1 — Original',
    description: 'The original guided question flow',
  },
];

function GuidedInterviewExperienceSelector({
  compact,
  value,
  onChange,
}: GuidedInterviewExperienceSelectorProps): React.JSX.Element {
  const selected = GUIDED_INTERVIEW_OPTIONS.find((option) => option.value === value);

  return (
    <div className={`${compact ? 'mt-2' : 'mt-3'} flex flex-wrap items-center gap-2`}>
      <label htmlFor="guided-interview-version" className="text-[11px] font-medium text-white/45">
        Interview version
      </label>
      <Select
        id="guided-interview-version"
        aria-label="Guided interview version"
        value={value}
        onChange={(event) => onChange(event.target.value as GuidedInterviewVersion)}
        className="h-8 w-auto min-w-64 py-1 text-[11px]"
      >
        {GUIDED_INTERVIEW_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
            {option.value === RECOMMENDED_GUIDED_INTERVIEW_VERSION ? ' (Recommended)' : ''}
          </option>
        ))}
      </Select>
      <span className="min-w-48 flex-1 text-[10px] text-white/35">{selected?.description}</span>
    </div>
  );
}

export function CreateKickoffSelector({
  mode,
  onModeChange,
  guidedInterviewVersion,
  onGuidedInterviewVersionChange,
  onSubmit,
  submitDisabled,
  seedReady = true,
  designDisabledReason,
  compact = false,
  presentation = 'default',
  secondaryAction,
  submitButtonRef,
  actionDisabledReason,
  onActionDisabledHelp,
}: {
  mode: VisibleCreateKickoffMode;
  onModeChange: (mode: VisibleCreateKickoffMode) => void;
  guidedInterviewVersion: GuidedInterviewVersion;
  onGuidedInterviewVersionChange: (version: GuidedInterviewVersion) => void;
  onSubmit: () => void;
  submitDisabled: boolean;
  seedReady?: boolean;
  designDisabledReason?: string;
  compact?: boolean;
  presentation?: 'default' | 'workbench';
  secondaryAction?: ReactNode;
  submitButtonRef?: Ref<HTMLButtonElement>;
  actionDisabledReason?: string;
  onActionDisabledHelp?: () => void;
}): React.JSX.Element {
  const labelId = useId();
  const designReasonId = useId();
  const selected = MODE_COPY[mode];
  const selectedUnavailable = mode === 'advanced' && designDisabledReason !== undefined;

  return (
    <section
      className={`w-full ${presentation === 'workbench' ? 'flex h-full min-h-0 flex-col' : ''}`}
      aria-labelledby={labelId}
      data-testid="create-kickoff-selector"
    >
      <p
        id={labelId}
        className={`${compact ? 'mb-1.5 text-xs' : 'mb-2 text-[13px]'} font-medium text-white/65`}
      >
        Start with
      </p>
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        className="grid grid-cols-1 divide-y divide-white/[0.12] overflow-hidden rounded-xl border border-white/[0.12] sm:grid-cols-2 sm:divide-x sm:divide-y-0"
      >
        <button
          type="button"
          role="radio"
          aria-checked={mode === 'quick'}
          onClick={() => onModeChange('quick')}
          className={`relative flex min-w-0 items-center gap-3 px-4 text-left transition-colors focus-visible:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-crucible-accent ${
            compact ? 'min-h-14 py-2.5' : 'min-h-[68px] py-3'
          } ${
            mode === 'quick'
              ? 'z-10 bg-crucible-accent/[0.08] text-white outline outline-1 -outline-offset-1 outline-crucible-accent'
              : 'text-white/70 hover:bg-white/[0.04]'
          }`}
        >
          <MessageCircleMore size={compact ? 18 : 21} strokeWidth={1.7} className="shrink-0" />
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium`}>
              {MODE_COPY.quick.label}
            </span>
          </span>
        </button>

        <button
          type="button"
          role="radio"
          aria-checked={mode === 'advanced'}
          aria-describedby={designDisabledReason === undefined ? undefined : designReasonId}
          disabled={designDisabledReason !== undefined}
          title={designDisabledReason ?? MODE_COPY.advanced.description}
          onClick={() => onModeChange('advanced')}
          className={`relative flex min-w-0 items-center gap-3 px-4 text-left transition-colors focus-visible:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-crucible-accent ${
            compact ? 'min-h-14 py-2.5' : 'min-h-[68px] py-3'
          } ${
            mode === 'advanced'
              ? 'z-10 bg-crucible-accent/[0.08] text-white outline outline-1 -outline-offset-1 outline-crucible-accent'
              : 'text-white/70 hover:bg-white/[0.04]'
          } disabled:cursor-not-allowed disabled:bg-white/[0.015] disabled:text-white/30 disabled:hover:bg-white/[0.015]`}
        >
          <PenLine size={compact ? 18 : 21} strokeWidth={1.7} className="shrink-0" />
          <span className="flex min-w-0 flex-col">
            <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium`}>
              {MODE_COPY.advanced.label}
            </span>
            {designDisabledReason !== undefined && (
              <span className="text-[11px] leading-tight text-white/30">This Mac only</span>
            )}
          </span>
        </button>
      </div>

      {mode === 'quick' && (
        <GuidedInterviewExperienceSelector
          compact={compact}
          value={guidedInterviewVersion}
          onChange={onGuidedInterviewVersionChange}
        />
      )}

      {designDisabledReason !== undefined && (
        <p id={designReasonId} className="mt-2 text-[11px] leading-relaxed text-white/35">
          {designDisabledReason}
        </p>
      )}

      {presentation === 'workbench' ? (
        <>
          <p className="mt-3 min-w-0 text-xs leading-relaxed text-white/45">
            {selectedUnavailable
              ? designDisabledReason
              : actionDisabledReason
                ? actionDisabledReason
                : mode === 'quick' && !seedReady
                  ? 'Add a description, screenshot, or document to begin the interview.'
                  : selected.description}
          </p>
          {actionDisabledReason && onActionDisabledHelp && (
            <button
              type="button"
              onClick={onActionDisabledHelp}
              className="mt-2 self-start text-xs text-crucible-accent-hover underline underline-offset-2"
            >
              Open Settings
            </button>
          )}
          <div className="mt-auto pb-[clamp(3rem,17vh,10rem)] pt-8">
            <button
              ref={submitButtonRef}
              type="button"
              onClick={onSubmit}
              disabled={submitDisabled || selectedUnavailable}
              data-testid="new-game-create-button"
              className="inline-flex min-h-16 w-full items-center justify-center gap-3 rounded-xl bg-crucible-accent px-5 text-sm font-medium text-black transition-colors hover:bg-crucible-accent-hover disabled:cursor-not-allowed disabled:bg-white/[0.08] disabled:text-white/30"
              title={selectedUnavailable ? designDisabledReason : selected.description}
            >
              {selected.action}
              <ArrowRight size={16} strokeWidth={1.8} />
            </button>
            {secondaryAction}
          </div>
        </>
      ) : (
        <div
          className={`mt-2.5 flex gap-3 ${compact ? 'flex-col items-stretch' : 'flex-col items-stretch sm:flex-row sm:items-start'}`}
        >
          <p className="min-w-0 flex-1 text-xs leading-relaxed text-white/45">
            {selectedUnavailable
              ? designDisabledReason
              : actionDisabledReason
                ? actionDisabledReason
                : mode === 'quick' && !seedReady
                  ? 'Add a description, screenshot, or document to begin the interview.'
                  : selected.description}
          </p>
          {actionDisabledReason && onActionDisabledHelp && (
            <button
              type="button"
              onClick={onActionDisabledHelp}
              className="shrink-0 text-xs text-crucible-accent-hover underline underline-offset-2"
            >
              Open Settings
            </button>
          )}
          <button
            ref={submitButtonRef}
            type="button"
            onClick={onSubmit}
            disabled={submitDisabled || selectedUnavailable}
            data-testid="new-game-create-button"
            className={`inline-flex shrink-0 items-center justify-center gap-3 rounded-xl bg-crucible-accent font-medium text-black transition-colors hover:bg-crucible-accent-hover disabled:cursor-not-allowed disabled:bg-white/[0.08] disabled:text-white/30 ${
              compact ? 'min-h-10 px-4 text-xs' : 'min-h-11 px-5 text-sm'
            }`}
            title={selectedUnavailable ? designDisabledReason : selected.description}
          >
            {selected.action}
            <ArrowRight size={16} strokeWidth={1.8} />
          </button>
        </div>
      )}
    </section>
  );
}
